// Pop-up dedicado de UMA anotação — aberto pelo hub "Minhas anotações"
// (AnotacoesHubView). Diferente de NotesWindow (que mostra todas as notas
// de um banco), aqui é uma única nota, em fullscreen.
//
// Sincronia com a aba principal (hub):
//   - BroadcastChannel "note-window": eventos open/heartbeat/close + noteId
//   - localStorage fallback `note-window-active:<noteId>` com timestamp
//   - Hub usa useSingleNoteWindowOpen(id) pra desabilitar o card enquanto
//     o pop-up estiver vivo, evitando edição simultânea.
//
// [[project-notes-hub-popout]]

import { useEffect, useState } from "preact/hooks";
import { StickyNote, X, AlertCircle, Loader2, FileDown } from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { exportSingleNotePdf, NoteEditor, type Note } from "./CadernoNotesPanel";
import { ConfirmRoot, PromptRoot } from "./ConfirmPromptModal";
import { ShareDialog } from "./ShareDialog";

const HEARTBEAT_MS = 5_000;
const LOCAL_STORAGE_PREFIX = "note-window-active:";
const BROADCAST_CHANNEL = "note-window";

function broadcast(event: "open" | "close" | "heartbeat", noteId: string) {
  try {
    const bc = new BroadcastChannel(BROADCAST_CHANNEL);
    bc.postMessage({ event, noteId, t: Date.now() });
    bc.close();
  } catch {
    /* sem BroadcastChannel — fallback localStorage no useEffect abaixo */
  }
}

export function SingleNoteWindow({ noteId }: { noteId: string }) {
  const [note, setNote] = useState<Note | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);

  // Carrega a nota uma vez no mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await call<{ note: Note }>(
          `/api/student/notes?id=${encodeURIComponent(noteId)}`
        );
        if (!cancelled) {
          setNote(res.note);
          setPhase("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setErr(friendlyError(e));
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [noteId]);

  // Heartbeat + open/close broadcast (mesmo padrão do NotesWindow).
  useEffect(() => {
    broadcast("open", noteId);
    try {
      localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${noteId}`, String(Date.now()));
    } catch {
      /* ignore */
    }
    const interval = window.setInterval(() => {
      broadcast("heartbeat", noteId);
      try {
        localStorage.setItem(
          `${LOCAL_STORAGE_PREFIX}${noteId}`,
          String(Date.now())
        );
      } catch {
        /* ignore */
      }
    }, HEARTBEAT_MS);
    function onUnload() {
      broadcast("close", noteId);
      try {
        localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${noteId}`);
      } catch {
        /* ignore */
      }
    }
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      onUnload();
    };
  }, [noteId]);

  async function patch(p: Partial<Note>) {
    if (!note) return;
    try {
      const res = await call<{ note: Note }>(
        `/api/student/notes?id=${encodeURIComponent(noteId)}`,
        { method: "PATCH", body: JSON.stringify(p) }
      );
      setNote(res.note);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  async function del() {
    // Pop-up não confirma com APAGAR — a janela é uma "área de trabalho"
    // focada. Deleção fica no hub, onde tem o fluxo de confirmação. Aqui
    // ignoramos o botão Trash do editor pra evitar deleção acidental cross-window.
    setErr("Pra apagar, volta no hub de anotações.");
  }

  return (
    <div class="flex h-screen w-screen flex-col bg-background text-foreground">
      <header class="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card/40 px-4 py-2">
        <div class="flex min-w-0 items-center gap-2">
          <div class="grid size-8 shrink-0 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/20">
            <StickyNote className="size-4" />
          </div>
          <div class="min-w-0 leading-tight">
            <div class="truncate text-sm font-bold">
              {note?.title || "Anotação"}
            </div>
            <div class="text-[11px] text-muted-foreground">
              Janela dedicada · auto-save ativo
            </div>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void exportSingleNotePdf(noteId)}
            disabled={phase !== "ready"}
            title="Salvar esta anotação em PDF"
            aria-label="Exportar em PDF"
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FileDown className="size-3.5" />
            PDF
          </button>
          <button
            type="button"
            onClick={() => window.close()}
            title="Fechar"
            aria-label="Fechar janela"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      </header>
      <main class="flex min-h-0 flex-1 flex-col">
        {phase === "loading" && (
          <div class="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Carregando…
          </div>
        )}
        {phase === "error" && (
          <div class="m-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mr-2 inline size-4" />
            {err ?? "Erro ao carregar."}
          </div>
        )}
        {phase === "ready" && note && (
          <>
            {err && (
              <div class="mx-4 mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                {err}
              </div>
            )}
            <div class="flex min-h-0 flex-1 flex-col">
              <NoteEditor
                key={note.id}
                note={note}
                onPatch={(p) => void patch(p)}
                onDelete={() => void del()}
                isPopout
              />
            </div>
          </>
        )}
      </main>
      {/* Hosts dos modais usados pelo NoteEditor (inserir tabela, link, equação,
          apagar, compartilhar). O App.tsx monta esses no shell principal, mas
          como SingleNoteWindow é early return em apps/student-new/src/App.tsx,
          a gente precisa replicar aqui pra os prompts/confirmações terem onde
          renderizar dentro da janela popup. [[project-notes-hub-popout]] */}
      <ConfirmRoot />
      <PromptRoot />
      <ShareDialog />
    </div>
  );
}

/** Hook pro hub saber se EXISTE pop-up aberto pra essa nota. Usado pra
 *  desabilitar o card no grid enquanto a janela avulsa estiver viva. */
export function useSingleNoteWindowOpen(noteId: string | null): boolean {
  const [open, setOpen] = useState<boolean>(false);
  useEffect(() => {
    if (!noteId) {
      setOpen(false);
      return;
    }
    let aliveTimeout: ReturnType<typeof setTimeout> | null = null;
    function markAlive() {
      setOpen(true);
      if (aliveTimeout) clearTimeout(aliveTimeout);
      aliveTimeout = setTimeout(() => setOpen(false), HEARTBEAT_MS * 2 + 1000);
    }
    function markDead() {
      if (aliveTimeout) clearTimeout(aliveTimeout);
      setOpen(false);
    }
    try {
      const ts = parseInt(
        localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${noteId}`) ?? "0",
        10
      );
      if (ts && Date.now() - ts < HEARTBEAT_MS * 3) markAlive();
    } catch {
      /* ignore */
    }
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(BROADCAST_CHANNEL);
      bc.onmessage = (ev) => {
        const data = ev.data as { event?: string; noteId?: string };
        if (data?.noteId !== noteId) return;
        if (data.event === "open" || data.event === "heartbeat") markAlive();
        else if (data.event === "close") markDead();
      };
    } catch {
      /* sem BroadcastChannel */
    }
    const poll = window.setInterval(() => {
      try {
        const ts = parseInt(
          localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${noteId}`) ?? "0",
          10
        );
        if (ts && Date.now() - ts < HEARTBEAT_MS * 3) markAlive();
        else markDead();
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => {
      window.clearInterval(poll);
      if (bc) bc.close();
      if (aliveTimeout) clearTimeout(aliveTimeout);
    };
  }, [noteId]);
  return open;
}

/** Abre o pop-up de UMA nota numa janela nova. Idempotente — se já estiver
 *  aberta com o mesmo nome de window, traz pra frente em vez de duplicar. */
export function openNoteInWindow(noteId: string): void {
  const url = `/student/?view=note-window&noteId=${encodeURIComponent(noteId)}`;
  window.open(url, `note-${noteId}`, "width=720,height=860,resizable=yes,scrollbars=yes");
}

/** Hook que devolve o conjunto de noteIds com pop-up ATIVO agora. Usado
 *  pelo hub pra FILTRAR cards (some quem está em janela). Re-scaneia
 *  localStorage a cada 3s e responde ao BroadcastChannel pra reagir mais
 *  rápido. */
export function useOpenNoteWindowIds(): Set<string> {
  const [ids, setIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    function scan(): Set<string> {
      const out = new Set<string>();
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key || !key.startsWith(LOCAL_STORAGE_PREFIX)) continue;
          const ts = parseInt(localStorage.getItem(key) ?? "0", 10);
          if (ts && Date.now() - ts < HEARTBEAT_MS * 3) {
            out.add(key.slice(LOCAL_STORAGE_PREFIX.length));
          }
        }
      } catch {
        /* ignore */
      }
      return out;
    }
    function refresh() {
      const next = scan();
      setIds((prev) => {
        if (prev.size === next.size && [...prev].every((x) => next.has(x))) return prev;
        return next;
      });
    }
    refresh();
    const poll = window.setInterval(refresh, 3000);
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(BROADCAST_CHANNEL);
      bc.onmessage = () => refresh();
    } catch {
      /* sem BroadcastChannel */
    }
    return () => {
      window.clearInterval(poll);
      if (bc) bc.close();
    };
  }, []);

  return ids;
}
